import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useOutletContext, useParams } from 'react-router-dom'
import type { PointsOutletContext } from './ModernPointsLayout'
import { getRuntimeTenantIdSync } from '../../lib/runtimeTenant'
import {
  fetchScratchProductPlayConfig,
  fetchMyScratchPurchases,
  fetchMyScratchStats,
  purchaseScratchTicket,
  revealScratchPurchase,
  isScratchPurchasePending,
  classifyScratchNet,
  type ScratchPlayConfig,
  type ScratchPurchase,
  type ScratchRevealResult,
  type ScratchCell,
  type ScratchPlayerStats,
} from '../../services/scratchLotteryService'
import { resolveScratchTheme } from './scratchLotteryThemes'
import './scratchLottery.css'

type Phase = 'loading' | 'idle' | 'scratching' | 'revealing' | 'revealed' | 'error'

const DEFAULT_WINNING_COUNT = 5
/** ค่าเริ่มต้นกริด YOUR NUMBERS = 5×5 ตามใบหวยขูดจริง */
const DEFAULT_CELL_COUNT = 25
const PLAY_GRID_COLUMNS = 5
/** ขูดถึงกี่ % ถึงถือว่าฟอยล์นั้นเปิดพอสำหรับไฮไลต์ */
const FOIL_OPEN_PERCENT = 78
/** ขูดทั้งบน+ล่างถึงระดับนี้แล้วค่อยเปิดการ์ดทั้งใบ */
const FINISH_BOTH_PERCENT = 80

const formatBaht = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** ยอดบนช่องการ์ด — สั้น อ่านง่าย ดูว้าว */
const formatPrizeLabel = (value: number | null | undefined) => {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n >= 1_000_000) {
    const m = n / 1_000_000
    return `${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}ล.฿`
  }
  if (n >= 1000) {
    return `${Math.round(n).toLocaleString('th-TH')}฿`
  }
  if (Number.isInteger(n)) return `${n}฿`
  return `${formatBaht(n)}฿`
}

function clampSlotCount(value: number | null | undefined, fallback: number, max: number): number {
  const n = Number(value)
  if (!Number.isInteger(n) || n < 1) return fallback
  return Math.min(max, n)
}

/** วงกลมเกจแบบเทน้ำ + คลื่นกระเพื่อม */
function JackpotGaugeRing({
  current,
  target,
  nextIsJackpot,
}: {
  current: number
  target: number
  nextIsJackpot: boolean
}) {
  const ratio = nextIsJackpot
    ? 1
    : target > 0
      ? Math.min(1, Math.max(0, current / target))
      : 0
  const fillPct = Math.round(ratio * 100)
  const label = nextIsJackpot ? target : current
  const levelClass =
    ratio >= 0.95 ? ' is-full' : ratio >= 0.7 ? ' is-high' : ratio >= 0.35 ? ' is-mid' : ' is-low'

  return (
    <div
      className={`scr-lotto__gauge${nextIsJackpot ? ' is-ready' : ''}${levelClass}`}
      style={{ ['--gauge-fill' as string]: `${fillPct}%` }}
      aria-label={`เกจแจ๊คพอต ${current} จาก ${target}`}
      role="img"
    >
      <div className="scr-lotto__gauge-bowl">
        <div className="scr-lotto__gauge-water" aria-hidden>
          <span className="scr-lotto__gauge-wave scr-lotto__gauge-wave--a" />
          <span className="scr-lotto__gauge-wave scr-lotto__gauge-wave--b" />
          <span className="scr-lotto__gauge-shine" />
          <span className="scr-lotto__gauge-bubbles">
            <i />
            <i />
            <i />
          </span>
        </div>
        <div className="scr-lotto__gauge-rim" aria-hidden />
      </div>
      <div className="scr-lotto__gauge-center">
        <strong>
          {label}
          <span>/{target}</span>
        </strong>
        <em>{nextIsJackpot ? 'พร้อม!' : 'เกจ'}</em>
      </div>
    </div>
  )
}

/** ยอดกลม ๆ สำหรับโชว์บนการ์ด (cosmetic — ไม่ผูก pool จริง) — จำนวนเต็มเท่านั้น */
const NICE_PRIZE_STEPS = [
  1, 2, 5, 10, 20, 30, 50, 80, 100, 150, 200, 300, 500, 800, 1000, 1500, 2000, 3000, 5000, 8000, 10000,
  15000, 20000, 30000, 50000, 80000, 100000, 150000, 200000, 300000, 500000, 800000, 1000000,
]

function resolvePrizeBand(ticketPrice: number, minPrize?: number, maxJackpot?: number) {
  const lo = Math.max(10, Number(minPrize) || Math.max(10, ticketPrice * 0.5))
  const hi = Math.max(lo * 5, Number(maxJackpot) || Math.max(ticketPrice * 200, 10_000))
  return { lo, hi }
}

function roundBaht(value: number): number {
  return Math.round(Number(value) * 100) / 100
}

function wholeBaht(value: number): number {
  return Math.max(0, Math.round(Number(value) || 0))
}

function playScratchTone(
  ctx: AudioContext,
  frequency: number,
  duration: number,
  volume: number,
  delay = 0,
  type: OscillatorType = 'triangle',
) {
  const start = ctx.currentTime + delay
  const oscillator = ctx.createOscillator()
  const gain = ctx.createGain()
  oscillator.type = type
  oscillator.frequency.setValueAtTime(frequency, start)
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(80, frequency * 0.55), start + duration)
  gain.gain.setValueAtTime(0.0001, start)
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), start + 0.006)
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
  oscillator.connect(gain)
  gain.connect(ctx.destination)
  oscillator.start(start)
  oscillator.stop(start + duration + 0.03)
}

/** เสียงเหรียญหล่น / เงินตก แบบสังเคราะห์ — ไม่ต้องมีไฟล์เสียง */
function playCoinRainSound(ctx: AudioContext) {
  if (ctx.state === 'suspended') void ctx.resume()
  for (let i = 0; i < 16; i += 1) {
    const delay = i * 0.065 + (i % 3) * 0.012
    const freq = 1400 + (i % 5) * 180 + (i % 2) * 90
    playScratchTone(ctx, freq, 0.07 + (i % 4) * 0.015, 0.045 + (i % 3) * 0.01, delay, 'triangle')
    if (i % 3 === 0) {
      playScratchTone(ctx, freq * 1.35, 0.04, 0.03, delay + 0.01, 'square')
    }
  }
  // ถุงเงินตกท้าย
  playScratchTone(ctx, 220, 0.28, 0.05, 0.95, 'sine')
  playScratchTone(ctx, 330, 0.22, 0.04, 1.05, 'triangle')
}

type FallingCoin = {
  id: number
  left: number
  delay: number
  duration: number
  size: number
  spin: number
  sway: number
}

function buildFallingCoins(seed: number, count = 22): FallingCoin[] {
  const rand = mulberry32(seed >>> 0 || 1)
  return Array.from({ length: count }, (_, id) => ({
    id,
    left: 6 + rand() * 88,
    delay: rand() * 0.85,
    duration: 1.15 + rand() * 1.1,
    size: 14 + rand() * 16,
    spin: 180 + rand() * 540,
    sway: (rand() - 0.5) * 48,
  }))
}

function pickOneNicePrize(rand: () => number, lo: number, hi: number, biasHigh: boolean): number {
  const pool = NICE_PRIZE_STEPS.filter((v) => v >= lo && v <= hi)
  const steps = pool.length > 0 ? pool : [Math.round(lo), Math.round((lo + hi) / 2), Math.round(hi)]
  const t = biasHigh ? 0.45 + rand() * 0.55 : rand()
  const idx = Math.min(steps.length - 1, Math.floor(t * steps.length))
  return steps[idx] ?? Math.round(hi)
}

function shuffleInPlace<T>(items: T[], rand: () => number): T[] {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1))
    const tmp = items[i]!
    items[i] = items[j]!
    items[j] = tmp
  }
  return items
}

/** จำนวนคู่ถูกที่อยากได้ — ยอดเล็กจำกัดไม่ให้แตกเยอะเกิน */
function chooseMatchCount(rand: () => number, maxPairs: number, prizeWhole?: number): number {
  let cap = Math.max(1, Math.min(4, maxPairs))
  if (prizeWhole != null && Number.isFinite(prizeWhole)) {
    cap = Math.min(cap, Math.max(1, prizeWhole))
    if (prizeWhole <= 10) cap = Math.min(cap, 2)
    else if (prizeWhole <= 30) cap = Math.min(cap, 3)
  }
  if (cap <= 1) return 1
  const roll = rand()
  if (cap >= 4 && roll < 0.18) return 4
  if (cap >= 3 && roll < 0.5) return 3
  if (cap >= 2 && roll < 0.9) return 2
  return 1
}

/**
 * แตก prizeAmount เป็นจำนวนเต็มเท่านั้น รวมกันได้พอดี
 * ถ้าแตก n ส่วนไม่ได้ จะลดจำนวนคู่ลงเอง (เช่น 10 → [5,5] หรือ [10])
 */
function splitPrizeAmount(total: number, parts: number, rand: () => number): number[] {
  const t = wholeBaht(total)
  if (!(t > 0)) return [0]
  let n = Math.min(Math.max(1, Math.floor(parts)), t)

  while (n > 1) {
    const steps = NICE_PRIZE_STEPS.filter((s) => Number.isInteger(s) && s >= 1 && s < t)
    for (let attempt = 0; attempt < 40 && steps.length > 0; attempt += 1) {
      const out: number[] = []
      let remaining = t
      let ok = true
      for (let i = 0; i < n - 1; i += 1) {
        const slotsLeftAfter = n - i - 1
        const maxPart = remaining - slotsLeftAfter
        const candidates = steps.filter((s) => s <= maxPart)
        if (candidates.length === 0) {
          ok = false
          break
        }
        const pick = candidates[Math.floor(rand() * candidates.length)]!
        out.push(pick)
        remaining -= pick
      }
      if (!ok || remaining < 1) continue
      if (!Number.isInteger(remaining)) continue
      out.push(remaining)
      if (out.reduce((sum, v) => sum + v, 0) === t && out.every((v) => v >= 1 && Number.isInteger(v))) {
        return shuffleInPlace(out, rand)
      }
    }

    // แบ่งจำนวนเต็มเท่า ๆ กัน (เช่น 10 / 2 → 5+5)
    const base = Math.floor(t / n)
    if (base >= 1) {
      const out = Array.from({ length: n }, () => base)
      let rem = t - base * n
      for (let i = 0; rem > 0; i += 1, rem -= 1) {
        out[i % n] += 1
      }
      return shuffleInPlace(out, rand)
    }
    n -= 1
  }

  return [t]
}

function pickUniqueNumber(rand: () => number, used: Set<number>): number {
  let value = Math.floor(rand() * 90) + 10
  let guard = 0
  while (used.has(value) && guard < 80) {
    value = Math.floor(rand() * 90) + 10
    guard += 1
  }
  used.add(value)
  return value
}

/** hash เสถียรจาก purchaseId เพื่อสร้างเลขหน้าการ์ด (cosmetic เท่านั้น ไม่ผูกกับรางวัลจริง) */
function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

interface CardFace {
  winningNumbers: Array<number | string>
  /** ยอดใต้เลขนำโชค — คู่ถูกใช้ยอดจริงหลัง reveal, ช่องหลอกใช้ cosmetic */
  winningDisplayPrizes: number[]
  /** ช่องดาวดำที่เป็นคู่ถูกรางวัลจริง */
  winningMatched: boolean[]
  cells: ScratchCell[]
}

interface PrizeBandInput {
  ticketPrice: number
  minPrize?: number
  maxJackpot?: number
}

/**
 * สร้างหน้าการ์ดแบบ deterministic จาก purchaseId
 * - เลขคงที่ตั้งแต่ซื้อจนเปิดผล
 * - หลายคู่ถูกได้ และราคาบน–ล่างของคู่เดียวกันต้องตรงกัน (จำนวนเต็ม)
 * - ผลรวมราคาคู่ถูก = prizeAmount
 */
function buildCardFace(
  purchaseId: string,
  reveal: Pick<ScratchRevealResult, 'prizeAmount' | 'winningNumbers' | 'cells'> | null,
  winningCount: number,
  cellCount: number,
  band: PrizeBandInput,
): CardFace {
  const { lo, hi } = resolvePrizeBand(band.ticketPrice, band.minPrize, band.maxJackpot)
  const rand = mulberry32(hashString(purchaseId))
  const prizeRand = mulberry32(hashString(`${purchaseId}:display-prizes`))
  const splitRand = mulberry32(hashString(`${purchaseId}:split`))

  // ถ้า server ส่ง cells ที่สอดคล้องมาแล้ว ใช้อันนั้น (ปัจจุบัน API ยังไม่ส่ง)
  if (reveal?.cells && reveal.cells.length > 0) {
    const cells = reveal.cells.map((cell, index) => ({
      ...cell,
      index: cell.index ?? index,
      matched: Boolean(cell.matched),
      displayPrizeAmount:
        cell.matched && Number(cell.prizeAmount) > 0
          ? Number(cell.prizeAmount)
          : Number(cell.displayPrizeAmount || cell.prizeAmount || 0),
    }))
    const matchedValues = cells.filter((c) => c.matched).map((c) => Number(c.value))
    const winningNumbers =
      reveal.winningNumbers && reveal.winningNumbers.length > 0
        ? reveal.winningNumbers
        : matchedValues
    const winningDisplayPrizes = winningNumbers.map((n) => {
      const matched = cells.find((c) => c.matched && String(c.value) === String(n))
      if (matched) return Number(matched.prizeAmount ?? matched.displayPrizeAmount ?? 0)
      return pickOneNicePrize(prizeRand, lo, hi, true)
    })
    const winningMatched = winningNumbers.map((n) =>
      cells.some((c) => c.matched && String(c.value) === String(n)),
    )
    return { winningNumbers, winningDisplayPrizes, winningMatched, cells }
  }

  const usedNumbers = new Set<number>()
  const winningNumbers: number[] = Array.from({ length: winningCount }, () =>
    pickUniqueNumber(rand, usedNumbers),
  )

  const prizeAmount = Number(reveal?.prizeAmount)
  const hasPrize = Number.isFinite(prizeAmount) && prizeAmount > 0
  const prizeWhole = hasPrize ? wholeBaht(prizeAmount) : undefined
  const preferredMatchCount = chooseMatchCount(
    rand,
    Math.min(winningCount, cellCount),
    prizeWhole,
  )
  const pairPrizes = hasPrize
    ? splitPrizeAmount(prizeAmount, preferredMatchCount, splitRand)
    : null
  const matchCount = pairPrizes?.length ?? preferredMatchCount

  const winSlotOrder = shuffleInPlace(
    Array.from({ length: winningCount }, (_, i) => i),
    rand,
  )
  const matchWinSlots = winSlotOrder.slice(0, matchCount)
  const cellSlotOrder = shuffleInPlace(
    Array.from({ length: cellCount }, (_, i) => i),
    rand,
  )
  const matchCellIndices = cellSlotOrder.slice(0, matchCount)

  const winningMatched = Array.from({ length: winningCount }, () => false)
  for (const slot of matchWinSlots) winningMatched[slot] = true

  // decoy prizes — สร้างครบทุกช่องก่อน แล้วค่อยทับคู่ถูกด้วยยอดจริง
  const decoyTopPrizes = Array.from({ length: winningCount }, () =>
    pickOneNicePrize(prizeRand, lo, hi, true),
  )
  const decoyCellPrizes = Array.from({ length: cellCount }, () =>
    pickOneNicePrize(prizeRand, lo, hi, false),
  )

  // ถ้ายังไม่รู้ยอดจริง (ไม่น่าเกิดหลัง expose prize ตอนซื้อ) ใช้ decoy ที่บน=ล่างของคู่ถูก
  const provisionalPairPrizes =
    pairPrizes ??
    Array.from({ length: matchCount }, () => pickOneNicePrize(prizeRand, lo, Math.min(hi, lo * 4), false))

  const winningDisplayPrizes = winningNumbers.map((_, i) => {
    const pairIdx = matchWinSlots.indexOf(i)
    if (pairIdx >= 0) return Number(provisionalPairPrizes[pairIdx] ?? 0)
    return decoyTopPrizes[i] ?? lo
  })

  const winSet = new Set(winningNumbers)
  const cells: ScratchCell[] = Array.from({ length: cellCount }, (_, index) => {
    let value = Math.floor(rand() * 90) + 10
    let guard = 0
    while (winSet.has(value) && guard < 40) {
      value = Math.floor(rand() * 90) + 10
      guard += 1
    }
    return {
      index,
      value,
      matched: false,
      displayPrizeAmount: decoyCellPrizes[index] ?? lo,
    }
  })

  matchWinSlots.forEach((winSlot, pairIdx) => {
    const cellIndex = matchCellIndices[pairIdx]!
    const value = winningNumbers[winSlot]!
    const amount = Number(provisionalPairPrizes[pairIdx] ?? 0)
    cells[cellIndex] = {
      index: cellIndex,
      value,
      matched: true,
      displayPrizeAmount: amount,
      ...(amount > 0 ? { prizeAmount: amount } : {}),
    }
  })

  return { winningNumbers, winningDisplayPrizes, winningMatched, cells }
}

function paintScratchFoil(
  canvas: HTMLCanvasElement,
  area: HTMLElement,
  label: string,
) {
  const rect = area.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) return
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = Math.round(rect.width * dpr)
  canvas.height = Math.round(rect.height * dpr)
  canvas.style.width = `${rect.width}px`
  canvas.style.height = `${rect.height}px`
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height)
  grad.addColorStop(0, '#d7dde6')
  grad.addColorStop(0.45, '#aeb7c4')
  grad.addColorStop(0.55, '#c9d1db')
  grad.addColorStop(1, '#8f99a8')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, rect.width, rect.height)
  ctx.fillStyle = 'rgba(255,255,255,0.35)'
  for (let i = 0; i < rect.width; i += 14) {
    for (let j = 0; j < rect.height; j += 14) {
      ctx.fillRect(i + ((j / 14) % 2 === 0 ? 0 : 7), j, 2, 2)
    }
  }
  ctx.fillStyle = 'rgba(30,58,138,0.55)'
  ctx.font = `700 ${Math.max(11, Math.min(14, rect.height * 0.28))}px "Kanit", system-ui, sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, rect.width / 2, rect.height / 2)
}

function useScratchCanvas(options: {
  enabled: boolean
  areaRef: React.RefObject<HTMLDivElement | null>
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  label: string
  threshold: number
  onThreshold?: () => void
  onProgress?: (percent: number) => void
}) {
  const { enabled, areaRef, canvasRef, label, threshold, onThreshold, onProgress } = options
  const drawingRef = useRef(false)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)
  const triggeredRef = useRef(false)
  const onThresholdRef = useRef(onThreshold)
  const onProgressRef = useRef(onProgress)

  useEffect(() => {
    onThresholdRef.current = onThreshold
    onProgressRef.current = onProgress
  })

  useEffect(() => {
    triggeredRef.current = false
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    const canvas = canvasRef.current
    const area = areaRef.current
    if (!canvas || !area) return

    const initFoil = () => {
      paintScratchFoil(canvas, area, label)
      onProgressRef.current?.(0)
    }

    const computeRatio = () => {
      const ctx = canvas.getContext('2d')
      if (!ctx) return 0
      const { width, height } = canvas
      try {
        const image = ctx.getImageData(0, 0, width, height)
        const data = image.data
        let cleared = 0
        let total = 0
        const step = 40
        for (let i = 3; i < data.length; i += step) {
          total += 1
          if (data[i] === 0) cleared += 1
        }
        return total === 0 ? 0 : cleared / total
      } catch {
        return 0
      }
    }

    const scratchAt = (x: number, y: number) => {
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.globalCompositeOperation = 'destination-out'
      ctx.lineWidth = 28
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      const last = lastPointRef.current
      ctx.beginPath()
      if (last) {
        ctx.moveTo(last.x, last.y)
        ctx.lineTo(x, y)
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.arc(x, y, 14, 0, Math.PI * 2)
      ctx.fill()
      lastPointRef.current = { x, y }
    }

    const raf = requestAnimationFrame(() => initFoil())

    const pointFromEvent = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const handleDown = (e: PointerEvent) => {
      drawingRef.current = true
      lastPointRef.current = null
      const p = pointFromEvent(e)
      scratchAt(p.x, p.y)
      try {
        canvas.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
    }
    const handleMove = (e: PointerEvent) => {
      if (!drawingRef.current) return
      const p = pointFromEvent(e)
      scratchAt(p.x, p.y)
    }
    const handleUp = () => {
      if (!drawingRef.current) return
      drawingRef.current = false
      lastPointRef.current = null
      const ratio = computeRatio()
      onProgressRef.current?.(Math.round(ratio * 100))
      if (ratio >= threshold && !triggeredRef.current) {
        triggeredRef.current = true
        onThresholdRef.current?.()
      }
    }
    const handleTouchMove = (e: TouchEvent) => {
      if (drawingRef.current) e.preventDefault()
    }

    canvas.addEventListener('pointerdown', handleDown)
    canvas.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    canvas.addEventListener('pointercancel', handleUp)
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('resize', initFoil)

    return () => {
      cancelAnimationFrame(raf)
      canvas.removeEventListener('pointerdown', handleDown)
      canvas.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      canvas.removeEventListener('pointercancel', handleUp)
      canvas.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('resize', initFoil)
    }
  }, [enabled, areaRef, canvasRef, label, threshold])
}

const ScratchLotteryGamePage: React.FC = () => {
  const { productId: productIdParam } = useParams<{ productId?: string }>()
  const productId = String(productIdParam || '').trim()
  const outlet = useOutletContext<PointsOutletContext | undefined>()
  const refreshUserSummary = outlet?.refreshUserSummary

  const [phase, setPhase] = useState<Phase>('loading')
  const [config, setConfig] = useState<ScratchPlayConfig | null>(null)
  const [purchase, setPurchase] = useState<ScratchPurchase | null>(null)
  const [reveal, setReveal] = useState<ScratchRevealResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [status, setStatus] = useState('')
  const [buying, setBuying] = useState(false)
  const [scratchPercent, setScratchPercent] = useState(0)
  const [winScratchPercent, setWinScratchPercent] = useState(0)
  const [animatedPrize, setAnimatedPrize] = useState(0)
  const [showPrizeBurst, setShowPrizeBurst] = useState(false)
  const [showPrizeAmount, setShowPrizeAmount] = useState(false)
  const [playerStats, setPlayerStats] = useState<ScratchPlayerStats | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const fallingCoins = useMemo(
    () => buildFallingCoins(hashString(purchase?.purchaseId || 'scratch-win'), 24),
    [purchase?.purchaseId],
  )

  const winAreaRef = useRef<HTMLDivElement | null>(null)
  const winCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const playAreaRef = useRef<HTMLDivElement | null>(null)
  const playCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const revealTriggeredRef = useRef(false)
  const idemKeyRef = useRef<string | null>(null)

  const idemStorageKey = useMemo(() => {
    const tenant = getRuntimeTenantIdSync() || 'default'
    return `htd_scratch_idem_${tenant}_${productId || 'default'}`
  }, [productId])

  const ticketPrice = Number(purchase?.ticketPrice ?? config?.ticketPrice ?? 0)
  const winningCount = clampSlotCount(config?.winningSlotCount, DEFAULT_WINNING_COUNT, 10)
  const cellCount = clampSlotCount(config?.playSlotCount, DEFAULT_CELL_COUNT, 40)
  const salesOpen =
    typeof config?.salesOpen === 'boolean'
      ? config.salesOpen
      : config?.userEntryVisible === true &&
        Number(config?.availableTickets ?? 0) > 0 &&
        Number(config?.activeBatches ?? 0) > 0

  /** หน้าการ์ดมีเลข+ยอดตั้งแต่ซื้อ — ขูดแล้วเห็นได้ทั้งบนและล่าง */
  const cardFace = useMemo<CardFace | null>(() => {
    if (!purchase) return null
    const knownPrize = Number(reveal?.prizeAmount ?? purchase.prizeAmount ?? 0)
    return buildCardFace(
      purchase.purchaseId,
      {
        prizeAmount: knownPrize > 0 ? knownPrize : 0,
        winningNumbers: reveal?.winningNumbers ?? purchase.winningNumbers,
        cells: reveal?.cells ?? purchase.cells,
      },
      winningCount,
      cellCount,
      {
        ticketPrice,
        minPrize: config?.minPrize,
        maxJackpot: config?.maxJackpot,
      },
    )
  }, [purchase, reveal, winningCount, cellCount, ticketPrice, config?.minPrize, config?.maxJackpot])

  const genIdemKey = useCallback(() => {
    if (idemKeyRef.current) return idemKeyRef.current
    let stored: string | null = null
    try {
      stored = window.localStorage.getItem(idemStorageKey)
    } catch {
      stored = null
    }
    const key =
      stored ||
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `scr-${Date.now()}-${Math.random().toString(36).slice(2)}`)
    idemKeyRef.current = key
    try {
      window.localStorage.setItem(idemStorageKey, key)
    } catch {
      /* ignore */
    }
    return key
  }, [idemStorageKey])

  const clearIdemKey = useCallback(() => {
    idemKeyRef.current = null
    try {
      window.localStorage.removeItem(idemStorageKey)
    } catch {
      /* ignore */
    }
  }, [idemStorageKey])

  const refreshPlayerStats = useCallback(async () => {
    try {
      const res = await fetchMyScratchStats(productId || undefined)
      if (res.success && res.data) setPlayerStats(res.data)
    } catch {
      /* stats optional */
    }
  }, [productId])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!productId) return
      setPhase('loading')
      setPurchase(null)
      setReveal(null)
      setErrorMessage(null)
      idemKeyRef.current = null
      revealTriggeredRef.current = false
      try {
        const cfgRes = await fetchScratchProductPlayConfig(productId)
        if (cancelled) return
        const cfg = cfgRes.data
        setConfig(cfg)

        let pending: ScratchPurchase | null = null
        try {
          const mine = await fetchMyScratchPurchases({ page: 1, limit: 30, productId })
          if (!cancelled) {
            pending =
              (mine.data || []).find(
                (p) => isScratchPurchasePending(p) && (!p.productId || p.productId === productId),
              ) || null
          }
        } catch {
          pending = null
        }

        if (cancelled) return
        if (pending) {
          setPurchase(pending)
          revealTriggeredRef.current = false
          setPhase('scratching')
          setStatus('พบการ์ดที่ยังไม่เปิดผล — ขูดเลขนำโชคและเลขของคุณได้เลย')
        } else {
          setPhase('idle')
        }
        void refreshPlayerStats()
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(e instanceof Error ? e.message : 'โหลดข้อมูลหวยขูดไม่สำเร็จ')
          setPhase('error')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [productId, refreshPlayerStats])

  const handleBuy = useCallback(async () => {
    if (buying || !salesOpen || !productId) return
    setBuying(true)
    setErrorMessage(null)
    setStatus('กำลังซื้อการ์ด...')
    try {
      const key = genIdemKey()
      const res = await purchaseScratchTicket(key, productId)
      const bought = res.data
      clearIdemKey()
      setReveal(null)
      setScratchPercent(0)
      setWinScratchPercent(0)
      setAnimatedPrize(0)
      setShowPrizeBurst(false)
      setShowPrizeAmount(false)
      revealTriggeredRef.current = false
      setPurchase(bought)
      setPhase('scratching')
      setStatus('ซื้อสำเร็จ — ขูดดาวดำ (เลขนำโชค) และกริดเลขของคุณ')
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'ซื้อการ์ดไม่สำเร็จ')
      setStatus('ซื้อการ์ดไม่สำเร็จ ลองอีกครั้งได้')
    } finally {
      setBuying(false)
    }
  }, [buying, salesOpen, productId, genIdemKey, clearIdemKey])

  const doReveal = useCallback(async () => {
    if (!purchase || revealTriggeredRef.current) return
    if (phase === 'revealed') return
    revealTriggeredRef.current = true
    setPhase('revealing')
    setStatus('กำลังเปิดผลรางวัล...')
    try {
      const res = await revealScratchPurchase(purchase.purchaseId)
      setReveal(res.data)
      setPurchase((prev) =>
        prev
          ? { ...prev, status: res.data.status || 'revealed', prizeAmount: res.data.prizeAmount }
          : prev,
      )
      setPhase('revealed')
      const prize = Number(res.data.prizeAmount || 0)
      setStatus(
        prize > 0
          ? `ยินดีด้วย! คุณได้รับรางวัล ${formatBaht(prize)} บาท`
          : 'เปิดผลเรียบร้อย',
      )
      if (refreshUserSummary) {
        void refreshUserSummary()
      }
      void refreshPlayerStats()
    } catch (e) {
      revealTriggeredRef.current = false
      setPhase('scratching')
      setErrorMessage(e instanceof Error ? e.message : 'เปิดผลไม่สำเร็จ')
      setStatus('เปิดผลไม่สำเร็จ ลองกดเปิดผลอีกครั้ง')
    }
  }, [purchase, refreshUserSummary, phase, refreshPlayerStats])

  const scratchEnabled = phase === 'scratching'

  useScratchCanvas({
    enabled: scratchEnabled,
    areaRef: winAreaRef,
    canvasRef: winCanvasRef,
    label: 'ขูดเลขนำโชค',
    threshold: FOIL_OPEN_PERCENT / 100,
    onProgress: setWinScratchPercent,
  })

  useScratchCanvas({
    enabled: scratchEnabled,
    areaRef: playAreaRef,
    canvasRef: playCanvasRef,
    label: 'ขูดตรงนี้เพื่อลุ้นรางวัล',
    // threshold สูง — ใช้แค่ progress; ไม่เปิดผลจากด้านล่างอย่างเดียว
    threshold: 0.99,
    onProgress: setScratchPercent,
  })

  // ต้องขูดทั้งบนและล่างพอสมควร ถึงจะเปิดการ์ดทั้งใบ (ไม่เปิดบนเพราะขูดล่างอย่างเดียว)
  useEffect(() => {
    if (phase !== 'scratching') return
    if (winScratchPercent < FINISH_BOTH_PERCENT || scratchPercent < FINISH_BOTH_PERCENT) return
    void doReveal()
  }, [phase, winScratchPercent, scratchPercent, doReveal])

  const everyTicketWins = config?.everyTicketWins !== false
  const prizeAmount = Number(reveal?.prizeAmount ?? purchase?.prizeAmount ?? 0)
  const roundTicketPrice = Number(purchase?.ticketPrice ?? ticketPrice)
  const netOutcome = classifyScratchNet(prizeAmount, roundTicketPrice)
  const balanceAfter =
    reveal?.balance?.after != null ? reveal.balance.after : reveal?.balanceAfter ?? null

  const theme = resolveScratchTheme(config?.theme)
  const faceOpen = phase === 'revealed' && Boolean(cardFace)
  const showNumbers = phase === 'scratching' || phase === 'revealing' || faceOpen
  /** ไฮไลต์คู่ถูกเมื่อขูดเปิดทั้งบนและล่างพอ — ไม่ผูกกับการเปิดการ์ดทั้งใบ */
  const topFoilOpen = faceOpen || winScratchPercent >= FOIL_OPEN_PERCENT
  const bottomFoilOpen = faceOpen || scratchPercent >= FOIL_OPEN_PERCENT
  const pairsUnlocked = topFoilOpen && bottomFoilOpen
  const winningNumbers =
    cardFace?.winningNumbers ?? Array.from({ length: winningCount }, () => '·')
  const winningDisplayPrizes = cardFace?.winningDisplayPrizes ?? []
  const winningMatched = cardFace?.winningMatched ?? []
  const gridCells = cardFace?.cells ?? []
  const emptyGridCells = useMemo<ScratchCell[]>(
    () =>
      Array.from({ length: cellCount }, (_, index) => ({
        index,
        value: '·',
        matched: false,
        displayPrizeAmount: 0,
      })),
    [cellCount],
  )

  // เหรียญหล่น + เสียงเงินตก แล้วค่อยนับยอดตรงกลางการ์ด
  useEffect(() => {
    if (phase !== 'revealed' || !(prizeAmount > 0)) {
      setShowPrizeBurst(false)
      setShowPrizeAmount(false)
      setAnimatedPrize(0)
      return
    }

    setShowPrizeBurst(true)
    setShowPrizeAmount(false)
    setAnimatedPrize(0)

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches

    try {
      const ctx = audioContextRef.current || new AudioContext()
      audioContextRef.current = ctx
      playCoinRainSound(ctx)
    } catch {
      /* ignore autoplay / unsupported */
    }

    const amountDelayMs = reduceMotion ? 120 : 700
    const countDurationMs = reduceMotion ? 400 : 1400
    let frame = 0
    let countStarted = false

    const amountTimer = window.setTimeout(() => {
      setShowPrizeAmount(true)
      countStarted = true
      const start = performance.now()
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / countDurationMs)
        const eased = 1 - (1 - t) ** 3
        setAnimatedPrize(roundBaht(prizeAmount * eased))
        if (t < 1) {
          frame = requestAnimationFrame(tick)
        } else {
          setAnimatedPrize(prizeAmount)
        }
      }
      frame = requestAnimationFrame(tick)
    }, amountDelayMs)

    return () => {
      window.clearTimeout(amountTimer)
      if (frame) cancelAnimationFrame(frame)
      if (!countStarted) setAnimatedPrize(0)
    }
  }, [phase, prizeAmount])

  useEffect(
    () => () => {
      void audioContextRef.current?.close()
      audioContextRef.current = null
    },
    [],
  )

  return (
    <div
      className="scr-lotto"
      style={
        {
          '--scr-accent': theme.accent,
          '--scr-accent-deep': theme.accent,
          '--scr-cols': String(PLAY_GRID_COLUMNS),
        } as React.CSSProperties
      }
    >
      {!productId ? <Navigate to="/points/games/scratch-lottery" replace /> : null}
      <div className="scr-lotto__sr" role="status" aria-live="polite">
        {status}
      </div>

      <header className="scr-lotto__hero">
        <div className="scr-lotto__logo" aria-hidden>
          <span className="scr-lotto__logo-mega">MEGA</span>
          <span className="scr-lotto__logo-sub">SCRATCH</span>
        </div>
        <div className="scr-lotto__hero-text">
          <h1 className="scr-lotto__title">{config?.name || 'หวยขูดเมกา'}</h1>
          <p className="scr-lotto__subtitle">
            ขูดเลขนำโชคด้านบน แล้วขูดเลขของคุณด้านล่าง — เลขตรงกันได้รางวัล
          </p>
          <Link to="/points/games/scratch-lottery" className="scr-lotto__switch-product">
            ← เปลี่ยนชุด
          </Link>
        </div>
      </header>

      {phase === 'loading' && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <i className="fas fa-spinner fa-spin scr-lotto__spinner" aria-hidden />
          <p>กำลังโหลดหวยขูด...</p>
        </div>
      )}

      {phase === 'error' && (
        <div className="scr-lotto__panel scr-lotto__panel--center">
          <p className="scr-lotto__error">{errorMessage || 'เกิดข้อผิดพลาด'}</p>
          <button
            type="button"
            className="scr-lotto__btn scr-lotto__btn--ghost"
            onClick={() => window.location.reload()}
          >
            ลองใหม่อีกครั้ง
          </button>
        </div>
      )}

      {phase !== 'loading' && phase !== 'error' && (
        <>
          {playerStats && (playerStats.ticketsBought > 0 || playerStats.jackpotGauge?.enabled) && (
            <section className="scr-lotto__stats" aria-label="สถิติการเล่นหวยขูด">
              {playerStats.jackpotGauge?.enabled && playerStats.jackpotGauge.target > 0 ? (
                <div className="scr-lotto__stats-top">
                  <JackpotGaugeRing
                    current={playerStats.jackpotGauge.current}
                    target={playerStats.jackpotGauge.target}
                    nextIsJackpot={playerStats.jackpotGauge.nextIsJackpot}
                  />
                  <div className="scr-lotto__gauge-copy">
                    <p className="scr-lotto__gauge-title">เกจแจ๊คพอต</p>
                    <p className="scr-lotto__gauge-desc">
                      {playerStats.jackpotGauge.nextIsJackpot
                        ? 'ใบถัดไปมีสิทธิ์รางวัลใหญ่สุดที่เหลือในชุด'
                        : `ซื้ออีก ${Math.max(0, playerStats.jackpotGauge.target - playerStats.jackpotGauge.current)} ใบ เพื่อลุ้นใบใหญ่สุดในชุด`}
                    </p>
                  </div>
                </div>
              ) : null}
              {playerStats.ticketsBought > 0 ? (
                <>
                  <div className="scr-lotto__stats-grid">
                    <div className="scr-lotto__stat">
                      <span>ซื้อแล้ว</span>
                      <strong>{playerStats.ticketsBought.toLocaleString('th-TH')} ใบ</strong>
                    </div>
                    <div className="scr-lotto__stat">
                      <span>ใช้ไป (เปิดผลแล้ว)</span>
                      <strong>{formatBaht(playerStats.totalSpent)} ฿</strong>
                    </div>
                    <div className="scr-lotto__stat">
                      <span>ได้รางวัลรวม</span>
                      <strong>{formatBaht(playerStats.totalPrizes)} ฿</strong>
                    </div>
                    <div className={`scr-lotto__stat${playerStats.netProfit >= 0 ? ' is-plus' : ' is-minus'}`}>
                      <span>สุทธิ</span>
                      <strong>
                        {playerStats.netProfit >= 0 ? '+' : ''}
                        {formatBaht(playerStats.netProfit)} ฿
                      </strong>
                    </div>
                  </div>
                  <p className="scr-lotto__stats-meta">
                    กำไร {playerStats.profitCount} · คุ้มทุน {playerStats.breakEvenCount} · ขาดทุน{' '}
                    {playerStats.lossCount}
                    {playerStats.pendingCount > 0 ? ` · รอเปิดผล ${playerStats.pendingCount}` : ''}
                  </p>
                </>
              ) : null}
            </section>
          )}

          <section
            className={`scr-lotto__card${showPrizeBurst ? ' is-celebrating' : ''}`}
            aria-label="การ์ดหวยขูดเมกา"
            style={{ backgroundImage: `url(${theme.bg})` }}
          >
            {showPrizeBurst && prizeAmount > 0 && (
              <div className="scr-lotto__win-burst" role="status" aria-live="polite">
                <div className="scr-lotto__coin-rain" aria-hidden>
                  {fallingCoins.map((coin) => (
                    <span
                      key={coin.id}
                      className="scr-lotto__coin"
                      style={
                        {
                          left: `${coin.left}%`,
                          animationDelay: `${coin.delay}s`,
                          animationDuration: `${coin.duration}s`,
                          width: `${coin.size}px`,
                          height: `${coin.size}px`,
                          '--scr-coin-spin': `${coin.spin}deg`,
                          '--scr-coin-sway': `${coin.sway}px`,
                        } as React.CSSProperties
                      }
                    />
                  ))}
                </div>
                <div className={`scr-lotto__win-burst-panel${showPrizeAmount ? ' is-visible' : ''}${
                  showPrizeAmount ? ` is-${netOutcome.kind}` : ''
                }`}>
                  <span className="scr-lotto__win-burst-label">คุณได้รับรางวัล</span>
                  <strong className="scr-lotto__win-burst-amount">
                    {formatBaht(animatedPrize)} <small>บาท</small>
                  </strong>
                  {showPrizeAmount && (
                    <span className={`scr-lotto__win-burst-net is-${netOutcome.kind}`}>
                      {netOutcome.label}{' '}
                      {netOutcome.net >= 0 ? '+' : ''}
                      {formatBaht(netOutcome.net)} บาท
                    </span>
                  )}
                </div>
              </div>
            )}

            <img
              className="scr-lotto__card-header"
              src={theme.header}
              alt=""
              draggable={false}
            />

            <div className="scr-lotto__ticket-top">
              <span className="scr-lotto__ticket-tag">MATCH &amp; WIN</span>
              <span className="scr-lotto__ticket-price">
                ราคา {formatBaht(ticketPrice)} บาท / ใบ
              </span>
            </div>

            <span className="scr-lotto__section-label">WINNING NUMBERS · ขูดดาวดำ</span>
            <div className="scr-lotto__winning" aria-label="เลขนำโชค">
              <div className="scr-lotto__winning-row">
                {winningNumbers.map((n, i) => {
                  const slotPrize = Number(winningDisplayPrizes[i] ?? 0)
                  const isMatchSlot = Boolean(winningMatched[i])
                  const showMatchWin = isMatchSlot && pairsUnlocked && slotPrize > 0
                  return (
                    <span
                      key={`win-${i}`}
                      className={`scr-lotto__ball${faceOpen ? ' is-revealed' : ''}${
                        showNumbers ? ' is-face-up' : ''
                      }${showMatchWin ? ' is-win' : ''}`}
                    >
                      {phase === 'idle' && (
                        <span className="scr-lotto__ball-star" aria-hidden>
                          <img src={theme.starWin} alt="" />
                        </span>
                      )}
                      {showNumbers ? (
                        <>
                          <span className="scr-lotto__ball-num">{String(n)}</span>
                          {slotPrize > 0 ? (
                            <span className="scr-lotto__ball-prize" aria-hidden>
                              {formatPrizeLabel(slotPrize)}
                            </span>
                          ) : (
                            <span className="scr-lotto__ball-word" aria-hidden>
                              #{i + 1}
                            </span>
                          )}
                        </>
                      ) : null}
                    </span>
                  )
                })}
              </div>

              {(phase === 'scratching' || phase === 'revealing') && (
                <div ref={winAreaRef} className="scr-lotto__scratch scr-lotto__scratch--win">
                  <canvas ref={winCanvasRef} className="scr-lotto__canvas" />
                </div>
              )}
            </div>

            <div className="scr-lotto__play">
              <span className="scr-lotto__section-label">
                YOUR NUMBERS · {cellCount} ช่อง
              </span>
              <div className="scr-lotto__grid-wrap">
                <div
                  className="scr-lotto__grid"
                  style={{
                    gridTemplateColumns: `repeat(${PLAY_GRID_COLUMNS}, 1fr)`,
                    gridTemplateRows: `repeat(${Math.ceil(Math.max(cellCount, 1) / PLAY_GRID_COLUMNS)}, minmax(58px, auto))`,
                  }}
                  aria-hidden={phase === 'scratching'}
                >
                  {(gridCells.length > 0 ? gridCells : emptyGridCells).map((cell) => {
                    const isMatchCell = Boolean(cell.matched)
                    const shownPrize = isMatchCell
                      ? Number(cell.prizeAmount ?? cell.displayPrizeAmount ?? 0)
                      : Number(cell.displayPrizeAmount ?? cell.prizeAmount ?? 0)
                    const showPrize = showNumbers && shownPrize > 0
                    const showMatchWin = isMatchCell && pairsUnlocked && shownPrize > 0
                    return (
                      <div
                        key={cell.index}
                        className={`scr-lotto__cell${showMatchWin ? ' is-win' : ''}${
                          showNumbers ? ' is-face-up' : ''
                        }`}
                      >
                        {phase === 'idle' && (
                          <span className="scr-lotto__cell-star" aria-hidden>
                            <img src={theme.starPlay} alt="" />
                          </span>
                        )}
                        {showNumbers ? (
                          <>
                            <span className="scr-lotto__cell-num">{String(cell.value)}</span>
                            {showPrize ? (
                              <span className={`scr-lotto__cell-prize${showMatchWin ? ' is-won' : ''}`}>
                                {formatPrizeLabel(shownPrize)}
                              </span>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    )
                  })}
                </div>

                {phase === 'idle' && (
                  <div className="scr-lotto__grid-cover scr-lotto__grid-cover--idle">
                    <i className="fas fa-lock" aria-hidden />
                    <span>ซื้อการ์ดเพื่อเริ่มขูด</span>
                  </div>
                )}

                {(phase === 'scratching' || phase === 'revealing') && (
                  <div ref={playAreaRef} className="scr-lotto__scratch scr-lotto__scratch--play">
                    <canvas ref={playCanvasRef} className="scr-lotto__canvas" />
                    {phase === 'revealing' && (
                      <div className="scr-lotto__revealing">
                        <i className="fas fa-spinner fa-spin" aria-hidden /> กำลังเปิดผล...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {phase === 'scratching' && (
                <div className="scr-lotto__progress" aria-hidden>
                  <div className="scr-lotto__progress-bar" style={{ width: `${scratchPercent}%` }} />
                </div>
              )}
            </div>

            <p className="scr-lotto__howto">
              วิธีเล่น: ขูด <b>ด้านบน</b> หรือ <b>ด้านล่าง</b> ก่อนก็ได้ — เลขตรงกันได้รางวัล
              (หลายคู่ได้ รวมเป็นยอดรางวัลจำนวนเต็ม)
            </p>

            <p className="scr-lotto__promise">
              {everyTicketWins ? 'ทุกใบมีรางวัล' : 'ลุ้นรับรางวัลได้ทุกใบ'} · เล่นเพื่อความบันเทิง
              โปรดเล่นอย่างมีสติ ไม่การันตีกำไร
            </p>
          </section>

          {phase === 'revealed' && reveal && balanceAfter != null && (
            <section className="scr-lotto__result" aria-label="ยอดคงเหลือ">
              <p className="scr-lotto__result-balance">
                ยอดคงเหลือ: <strong>{formatBaht(balanceAfter)} บาท</strong>
              </p>
            </section>
          )}

          <div className="scr-lotto__actions">
            {phase === 'idle' && salesOpen && (
              <button
                type="button"
                className="scr-lotto__btn scr-lotto__btn--buy"
                onClick={() => void handleBuy()}
                disabled={buying || ticketPrice <= 0}
              >
                {buying ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden /> กำลังซื้อ...
                  </>
                ) : (
                  <>
                    <i className="fas fa-ticket-alt" aria-hidden /> ซื้อการ์ด · {formatBaht(ticketPrice)} บาท
                  </>
                )}
              </button>
            )}

            {phase === 'idle' && !salesOpen && (
              <div className="scr-lotto__panel scr-lotto__panel--center">
                <p className="scr-lotto__closed-title">ปิดขายแล้ว</p>
                <p className="scr-lotto__closed-desc">
                  รอบนี้ปิดขายครบแล้ว — รอแอดมินเปิดชุดใบหวยใหม่
                </p>
              </div>
            )}

            {phase === 'scratching' && (
              <button
                type="button"
                className="scr-lotto__btn scr-lotto__btn--reveal"
                onClick={() => void doReveal()}
              >
                <i className="fas fa-eye" aria-hidden /> เปิดผลทั้งหมด
              </button>
            )}

            {phase === 'revealing' && (
              <button type="button" className="scr-lotto__btn scr-lotto__btn--reveal" disabled>
                <i className="fas fa-spinner fa-spin" aria-hidden /> กำลังเปิดผล...
              </button>
            )}

            {phase === 'revealed' && salesOpen && (
              <button
                type="button"
                className="scr-lotto__btn scr-lotto__btn--buy"
                onClick={() => {
                  setReveal(null)
                  setPurchase(null)
                  setScratchPercent(0)
                  setWinScratchPercent(0)
                  setAnimatedPrize(0)
                  setShowPrizeBurst(false)
                  setShowPrizeAmount(false)
                  revealTriggeredRef.current = false
                  setStatus('')
                  setPhase('idle')
                }}
                disabled={ticketPrice <= 0}
              >
                <i className="fas fa-redo" aria-hidden /> เล่นอีกใบ · {formatBaht(ticketPrice)} บาท
              </button>
            )}

            {phase === 'revealed' && !salesOpen && (
              <p className="scr-lotto__closed-desc">ปิดขายแล้ว — ไม่สามารถซื้อใบใหม่ได้ในตอนนี้</p>
            )}

            <Link to="/history/games/scratch-lottery" className="scr-lotto__btn scr-lotto__btn--ghost">
              <i className="fas fa-history" aria-hidden /> ประวัติหวยขูด
            </Link>
          </div>

          {errorMessage && <p className="scr-lotto__inline-error">{errorMessage}</p>}
        </>
      )}
    </div>
  )
}

export default ScratchLotteryGamePage
